import classNames from "classnames";
import orderBy from "lodash/orderBy";
import { useCallback, useEffect, useRef, useState } from "react";
import CustomButton from "rivals/components/shared/CustomButton";
import Drawer from "rivals/components/shared/Drawer";
import CloseIcon from "rivals/components/shared/Icons/Close";
import { timeToMoment } from "rivals/shared/utils/listVersions";
import { useIsMobileWidth } from "rivals/shared/utils/mediaQuery";
import { setData } from "rivals/shared/utils/qa";
import commonStyles from "rivals/styles/exports.module.scss";
import styles from "./index.module.scss";
import strings from "./strings";
import { Props } from "./types";

const {
  philter,
  selected,
  transferRankHistory,
  versions: versionsString
} = strings;

const VersioningDropdown = ({
  onSelect,
  preventClosing,
  selectedVersionId,
  versions
}: Props): JSX.Element => {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const toggleRef = useRef<HTMLButtonElement | null>(null);
  const matchesMobile = useIsMobileWidth();

  const handleOnClick = useCallback(
    (event: Event) => {
      if (toggleRef.current && menuRef.current) {
        const clickedOnMenu = event.composedPath().includes(menuRef.current);
        const clickedOnToggle = event
          .composedPath()
          .includes(toggleRef.current);

        if (!preventClosing && !clickedOnMenu && !clickedOnToggle) {
          setOpen(false);
        }
      }
    },
    [preventClosing]
  );

  useEffect(() => {
    if (open && !preventClosing) {
      document.addEventListener("click", handleOnClick);
    }

    return (): void => {
      if (open && !preventClosing) {
        document.removeEventListener("click", handleOnClick);
      }
    };
  }, [handleOnClick, open, preventClosing]);

  const handleToggleClick = useCallback(() => {
    setOpen(prevValue => !prevValue);
  }, []);

  const handleCloseIconClick = useCallback(() => {
    setOpen(false);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setOpen(false);
  }, []);

  const orderedVersions = orderBy(
    versions,
    version => timeToMoment(version, "updatedAt"),
    "desc"
  );

  const menu = (
    <div className={styles.menu} ref={menuRef}>
      <div className={styles.menuHeader}>
        <div className={styles.menuTitle}>{transferRankHistory}</div>
        <CustomButton
          ariaLabel="hide"
          className={styles.closeIconWrapper}
          icon={<CloseIcon fill={commonStyles.trout} />}
          onClick={handleCloseIconClick}
          philter={`${philter}-close`}
        />
      </div>
      <div className={styles.options}>
        {orderedVersions.map(version => {
          const { listId, updatedAt } = version;
          const isSelected = listId === selectedVersionId;

          const optionClassName = classNames(styles.option, {
            [styles.selectedOption]: isSelected
          });

          const handleVersionClick = (): void => {
            if (!isSelected) {
              onSelect(version);
              setOpen(false);
            }
          };

          return (
            <CustomButton
              buttonText={`${updatedAt} ${isSelected ? selected : ""}`}
              className={optionClassName}
              key={listId}
              onClick={handleVersionClick}
              philter={`${philter}-select-version`}
            />
          );
        })}
      </div>
    </div>
  );

  return (
    <div className={styles.container}>
      <button
        className={classNames(styles.toggle, { [styles.active]: open })}
        onClick={handleToggleClick}
        ref={toggleRef}
        {...setData(philter, "trigger")}
      >
        {versionsString}
        <svg
          className={styles.chevron}
          fill="none"
          height="8"
          viewBox="0 0 11 8"
          width="11"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            clipRule="evenodd"
            d="M8.93945 0.939227L10.3537 2.35344L5.64656 7.06055L0.939453 2.35344L2.35367 0.939227L5.64656 4.23212L8.93945 0.939227Z"
            fill="currentColor"
            fillRule="evenodd"
          />
        </svg>
      </button>
      <div className={classNames(styles.toggleable, { [styles.active]: open })}>
        {matchesMobile ? (
          <Drawer
            className={styles.mobileDrawer}
            closeIcon=""
            onClose={handleCloseDrawer}
            open={open}
            width={280}
          >
            {menu}
          </Drawer>
        ) : (
          menu
        )}
      </div>
    </div>
  );
};

export default VersioningDropdown;
